import { useCallback, useState } from 'react';
import { useUserId } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useAvailabilityRules } from '../store/hooks';
import type {
  AvailabilityEffect,
  AvailabilityRule,
  CreateAvailabilityRuleRequest,
} from '../types';

import { AvailabilityView } from './AvailabilityView';

/** Container: wires the caller's availability rules + mutations into the view. */
export function AvailabilitySection() {
  const userId = useUserId();
  const { rules, isLoading, error, createRule, removeRule } =
    useAvailabilityRules(userId);
  const { enqueueSnackbar } = useSnackbar();
  const { schedulingSettings, error: errorTracking } = useEventTracking();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreate = useCallback(
    async (payload: CreateAvailabilityRuleRequest) => {
      setIsSubmitting(true);
      try {
        await createRule(payload);
        schedulingSettings.availabilityRuleChanged({
          action: 'created',
          effect: payload.effect as AvailabilityEffect,
        });
        enqueueSnackbar('Availability added', { variant: 'success' });
      } catch {
        enqueueSnackbar('Failed to add availability', { variant: 'error' });
        errorTracking.track('Failed to add availability', 'availability_rule_create');
      } finally {
        setIsSubmitting(false);
      }
    },
    [createRule, schedulingSettings, enqueueSnackbar, errorTracking],
  );

  const handleDelete = useCallback(
    async (rule: AvailabilityRule) => {
      const id = rule.availability_rule_id;
      if (!id) return;
      setDeletingId(id);
      try {
        await removeRule(id);
        schedulingSettings.availabilityRuleChanged({
          action: 'deleted',
          effect: rule.effect as AvailabilityEffect,
        });
        enqueueSnackbar('Availability removed', { variant: 'success' });
      } catch {
        enqueueSnackbar('Failed to remove availability', { variant: 'error' });
        errorTracking.track('Failed to remove availability', 'availability_rule_delete');
      } finally {
        setDeletingId(null);
      }
    },
    [removeRule, schedulingSettings, enqueueSnackbar, errorTracking],
  );

  return (
    <AvailabilityView
      rules={rules}
      isLoading={isLoading}
      hasError={!!error}
      deletingId={deletingId}
      isSubmitting={isSubmitting}
      onCreate={handleCreate}
      onDelete={handleDelete}
    />
  );
}
